import { ReservationSettings } from '../models/reservation';

/**
 * Reservation times live on the restaurant's wall clock, not the viewer's:
 * reservation-service builds slots in the restaurant's `time_zone`, so every
 * helper here takes that IANA zone. Without one they fall back to the
 * browser's zone.
 */

/** Same fallback as reservation-service when a restaurant has no `time_zone`. */
export const DEFAULT_RESTAURANT_TIME_ZONE = 'America/Los_Angeles';

/** Wall-clock parts of an instant in a zone. */
function zonedParts(d: Date, timeZone?: string) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  };
}

/** The zone's offset from UTC at an instant, in milliseconds. */
function offsetMs(d: Date, timeZone?: string): number {
  const p = zonedParts(d, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(d.getTime() / 1000) * 1000;
}

/** YYYY-MM-DD of an instant on the zone's calendar. */
export function toServiceDate(d: Date, timeZone?: string): string {
  const p = zonedParts(d, timeZone);
  const m = String(p.month).padStart(2, '0');
  const day = String(p.day).padStart(2, '0');
  return `${p.year}-${m}-${day}`;
}

/** The instant the zone's clock reads HH:mm on the given service date. */
export function atTime(serviceDate: string, hhmm: string, timeZone?: string): Date {
  const [h, m] = hhmm.split(':').map(Number);
  const [y, mo, d] = serviceDate.split('-').map(Number);
  const wall = Date.UTC(y, mo - 1, d, h, m, 0, 0);
  // Two passes so a time just across a DST change picks up the right offset.
  const first = wall - offsetMs(new Date(wall), timeZone);
  return new Date(wall - offsetMs(new Date(first), timeZone));
}

/**
 * When a service period ends. An end at or before the start is on the next
 * day, as with 9 PM – 4 AM, the way reservation-service reads it (FNF-1278).
 */
export function periodEnd(
  serviceDate: string,
  period: { start: string; end: string },
  timeZone?: string,
): Date {
  const start = atTime(serviceDate, period.start, timeZone);
  const end = atTime(serviceDate, period.end, timeZone);
  if (end > start) {
    return end;
  }
  const [y, m, d] = serviceDate.split('-').map(Number);
  const nextDate = new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
  return atTime(nextDate, period.end, timeZone);
}

export function addMinutes(d: Date, minutes: number): Date {
  return new Date(d.getTime() + minutes * 60_000);
}

/** "6:30 PM" on the zone's clock. */
export function timeLabel(d: Date, timeZone?: string): string {
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone });
}

/** An instant formatted on the zone's clock with the given fields. */
export function formatInZone(
  d: Date,
  timeZone: string | undefined,
  options: Intl.DateTimeFormatOptions,
): string {
  return d.toLocaleString([], { ...options, timeZone });
}

/** Minutes a table is held for a party, from the settings' turn-time bands. */
export function turnTimeFor(settings: ReservationSettings, partySize: number): number {
  const bands = [...settings.turnTimes].sort((a, b) => a.maxParty - b.maxParty);
  return (bands.find((b) => partySize <= b.maxParty) ?? bands[bands.length - 1]).minutes;
}
